import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const SPECIALTIES = [
  "Cardiology", "Endocrinology", "Nephrology", "Neurology", "Gastroenterology",
  "Ophthalmology", "Orthopaedics", "Pulmonology", "Urology", "Dermatology",
  "ENT", "General Surgery", "Psychiatry", "Oncology", "Rheumatology", "Other",
];
const URGENCY = ["Routine", "Semi-urgent", "Urgent"];

export default function ReferralDialog({ patientId, visitId, defaultSummary, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    specialist_name: "",
    specialty: "Cardiology",
    clinic_or_hospital: "",
    reason: "",
    urgency: "Routine",
    clinical_summary: defaultSummary || "",
  });
  const nav = useNavigate();

  const submit = async () => {
    if (!form.specialty || !form.reason) { toast.error("Specialty and reason are required"); return; }
    try {
      const r = await api.post("/referrals", { patient_id: patientId, visit_id: visitId, ...form });
      toast.success("Referral created");
      setOpen(false);
      nav(`/referral/${r.data.referral.referral_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create referral");
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Send size={16} /> Create referral letter</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">SPECIALIST NAME</Label>
                <Input value={form.specialist_name} onChange={(e) => setForm({ ...form, specialist_name: e.target.value })} placeholder="Dr. …" data-testid="ref-specialist-name" />
              </div>
              <div>
                <Label className="overline">SPECIALTY *</Label>
                <select value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="h-9 w-full border border-slate-200 rounded px-2 text-sm" data-testid="ref-specialty">
                  {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="overline">CLINIC / HOSPITAL</Label>
              <Input value={form.clinic_or_hospital} onChange={(e) => setForm({ ...form, clinic_or_hospital: e.target.value })} placeholder="Optional" data-testid="ref-hospital" />
            </div>
            <div>
              <Label className="overline">REASON FOR REFERRAL *</Label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="ref-reason" />
            </div>
            <div>
              <Label className="overline">URGENCY</Label>
              <div className="mt-1 flex gap-2">
                {URGENCY.map((u) => (
                  <button key={u} onClick={() => setForm({ ...form, urgency: u })} className={`px-3 py-1 rounded-full text-xs border ${form.urgency === u ? "bg-pink-600 text-white border-pink-600" : "bg-white border-slate-200 hover:bg-slate-50"}`} data-testid={`ref-urgency-${u}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="overline">CLINICAL SUMMARY</Label>
              <Textarea rows={4} value={form.clinical_summary} onChange={(e) => setForm({ ...form, clinical_summary: e.target.value })} placeholder="Auto-populated from visit; edit as needed" data-testid="ref-summary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="ref-submit-btn">Create & Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
